import {
  Box,
  IconButton,
  Text,
  useTheme,
  styled,
} from '@ltvco/refresh-lib/theme';
import { Close } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { CsvPreviewBox } from './CsvPreviewBox';
import { PdfPreviewBox } from './PdfPreviewBox';

import type { FileKind } from './fileUtils';

const PreviewImage = styled('img')(({ theme }) => ({
  width: 140,
  height: 140,
  objectFit: 'cover',
  borderRadius: 8,
  cursor: 'pointer',
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    opacity: 0.8,
  },
}));

const FullscreenImage = styled('img')({
  maxWidth: '90%',
  maxHeight: '90%',
  objectFit: 'contain',
});

interface FilePreviewProps {
  kind: FileKind;
  previewUrl: string;
  fileName?: string;
  onRemove?: () => void;
}

export const FilePreview = ({
  kind,
  previewUrl,
  fileName,
  onRemove,
}: FilePreviewProps) => {
  const theme = useTheme();
  const [showFullscreen, setShowFullscreen] = useState(false);

  const handlePreviewClick = () => {
    if (kind === 'image') {
      setShowFullscreen(true);
    }
  };

  const handleCloseFullscreen = () => {
    setShowFullscreen(false);
  };

  useEffect(() => {
    if (!showFullscreen) {
      return;
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowFullscreen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showFullscreen]);

  const handleRemoveClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onRemove?.();
  };

  const handleImageStopPropagation = (
    e: React.MouseEvent<HTMLImageElement>
  ) => {
    e.stopPropagation();
  };

  return (
    <>
      <Box
        sx={{
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 0.5,
          maxWidth: 140,
        }}
      >
        <Box sx={{ position: 'relative' }}>
          {kind === 'image' && (
            <PreviewImage
              src={previewUrl}
              alt={fileName ?? 'File preview'}
              onClick={handlePreviewClick}
            />
          )}
          {kind === 'pdf' && <PdfPreviewBox size={140} iconSize={56} />}
          {kind === 'csv' && <CsvPreviewBox size={140} iconSize={56} />}
          {onRemove && (
            <IconButton
              onClick={handleRemoveClick}
              size="small"
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                backgroundColor: theme.palette.common.black,
                color: theme.palette.common.white,
                width: '14px !important',
                height: '14px !important',
                minWidth: 0,
                minHeight: 0,
                padding: 0,
                '&:hover': {
                  backgroundColor: theme.palette.common.black,
                  opacity: 0.85,
                },
              }}
              aria-label={`Remove ${kind}`}
            >
              <Close sx={{ fontSize: '8px !important' }} />
            </IconButton>
          )}
        </Box>
        {fileName && (
          <Text
            variant="caption"
            sx={{
              color: 'text.secondary',
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={fileName}
          >
            {fileName}
          </Text>
        )}
      </Box>

      {showFullscreen &&
        typeof document !== 'undefined' &&
        createPortal(
          <Box
            onClick={handleCloseFullscreen}
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 99999,
              cursor: 'pointer',
            }}
          >
            <FullscreenImage
              src={previewUrl}
              alt={fileName ?? 'Full size preview'}
              onClick={handleImageStopPropagation}
            />
            <IconButton
              onClick={handleCloseFullscreen}
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                color: 'white',
              }}
              aria-label="Close fullscreen"
            >
              <Close />
            </IconButton>
          </Box>,
          document.body
        )}
    </>
  );
};
